/* DITORINO: offer selector supporting independent color choices for each component of a 3-watch Shopify Bundles multipack. */
(() => {
  const rootSelector = '[data-quantity-offer]';
  const moneyFormatters = new Map();

  function money(cents, currency, locale) {
    const code = currency || 'BRL';
    const lang = locale || 'pt-BR';
    const key = `${lang}:${code}`;
    if (!moneyFormatters.has(key)) {
      moneyFormatters.set(key, new Intl.NumberFormat(lang, {
        style: 'currency', currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2
      }));
    }
    return moneyFormatters.get(key).format((Number(cents) || 0) / 100);
  }

  function selectedQuantity(root) {
    const selected = root.querySelector('[data-quantity-offer-option].is-selected');
    return selected ? Number(selected.dataset.quantity) : 1;
  }

  function parseJson(value, fallback) {
    try { return JSON.parse(value); } catch (_) { return fallback; }
  }

  function normalizedOptions(options) {
    return (Array.isArray(options) ? options : []).map((value) => String(value ?? '').trim().toLocaleLowerCase());
  }

  function updateBundleColorImages(root, sourceOptions) {
    const imageMap = parseJson(root.dataset.sourceColorImages || '[]', []);
    const productTitle = root.dataset.productTitle || 'Relógio';
    const defaultColor = Array.isArray(sourceOptions) ? String(sourceOptions[0] || '') : '';

    root.querySelectorAll('[data-bundle-color-select]').forEach((selector) => {
      const position = selector.dataset.bundleColorSelect;
      const image = root.querySelector(`[data-bundle-color-image="${position}"]`);
      if (!image) return;

      const selectedColor = String(selector.value || defaultColor).trim();
      const normalizedColor = selectedColor.toLocaleLowerCase();
      const match = imageMap.find((entry) => String(entry.color || '').trim().toLocaleLowerCase() === normalizedColor && entry.image);
      if (match && match.image) {
        if (image.getAttribute('src') !== match.image) image.setAttribute('src', match.image);
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.alt = `${productTitle} - ${selectedColor}`;
      }
    });
  }

  function bundleWantedOptions(root, sourceOptions) {
    const source = Array.isArray(sourceOptions) ? sourceOptions : [];
    const colorSelectors = Array.from(root.querySelectorAll('[data-bundle-color-select]'));
    if (Number(root.dataset.bundleOptionCount || 0) >= 3 && colorSelectors.length >= 2) {
      return [source[0] || '', colorSelectors[0].value || source[0] || '', colorSelectors[1].value || source[0] || ''];
    }
    return source;
  }

  function findBundleVariant(root, sourceOptions) {
    if (root.dataset.bundleConfigured !== 'true') return null;
    const variants = parseJson(root.dataset.bundleVariants || '[]', []);
    const wanted = normalizedOptions(bundleWantedOptions(root, sourceOptions));
    return variants.find((variant) => {
      const got = normalizedOptions(variant.options);
      return got.length === wanted.length && got.every((value, index) => value === wanted[index]);
    }) || null;
  }

  function updateBundleVariant(root, sourceOptions) {
    const variant = findBundleVariant(root, sourceOptions);
    root.dataset.sourceOptions = JSON.stringify(sourceOptions || []);
    updateBundleColorImages(root, sourceOptions);
    root.dataset.currentBundleVariantId = variant ? String(variant.id) : '';
    root.dataset.bundlePrice = variant && variant.price != null ? String(variant.price) : '';
    root.dataset.bundleAvailable = variant && variant.available ? 'true' : 'false';
    root.dataset.bundleInventoryManaged = variant && variant.inventory_managed ? 'true' : 'false';
    root.dataset.bundleInventoryQuantity = variant && variant.inventory_quantity != null ? String(variant.inventory_quantity) : '999999';
    root.dataset.bundleInventoryPolicy = variant && variant.inventory_policy ? String(variant.inventory_policy) : 'deny';
  }

  function renderPrices(root) {
    const base = Math.max(0, Math.round(Number(root.dataset.basePrice) || 0));
    const oneButton = root.querySelector('[data-quantity-offer-option][data-quantity="1"]');
    const bundleButton = root.querySelector('[data-quantity-offer-option][data-quantity="3"]');
    const oneTotal = oneButton && oneButton.querySelector('[data-tier-total]');
    if (oneTotal) oneTotal.textContent = money(base, root.dataset.currency, root.dataset.locale);

    if (bundleButton) {
      const bundlePrice = root.dataset.bundlePrice === '' ? null : Number(root.dataset.bundlePrice);
      const bundleTotal = bundleButton.querySelector('[data-tier-total]');
      const unitPrice = bundleButton.querySelector('[data-unit-price]');
      if (bundlePrice != null && Number.isFinite(bundlePrice)) {
        if (bundleTotal) bundleTotal.textContent = money(bundlePrice, root.dataset.currency, root.dataset.locale);
        if (unitPrice) unitPrice.textContent = `Cada unidade sai por ${money(Math.round(bundlePrice / 3), root.dataset.currency, root.dataset.locale)}`;
      } else {
        if (bundleTotal) bundleTotal.textContent = '';
        if (unitPrice) unitPrice.textContent = '';
      }
    }
  }

  function showBundleColorSelectors(root) {
    const panel = root.querySelector('[data-bundle-color-selectors]');
    if (panel) panel.hidden = selectedQuantity(root) !== 3;
  }

  function updateAvailability(root) {
    const oneButton = root.querySelector('[data-quantity-offer-option][data-quantity="1"]');
    const bundleButton = root.querySelector('[data-quantity-offer-option][data-quantity="3"]');
    const note = root.querySelector('[data-quantity-stock-note]');
    const originalAvailable = root.dataset.available !== 'false';
    const originalManaged = root.dataset.inventoryManaged === 'true';
    const originalPolicy = root.dataset.inventoryPolicy || 'deny';
    const originalQty = Number(root.dataset.inventoryQuantity || 0);
    const oneUnavailable = !originalAvailable || (originalManaged && originalPolicy !== 'continue' && originalQty < 1);

    if (oneButton) {
      oneButton.disabled = oneUnavailable;
      if (oneUnavailable) oneButton.setAttribute('aria-disabled', 'true');
      else oneButton.removeAttribute('aria-disabled');
    }

    let bundleUnavailable = true;
    let selectedComboUnavailable = true;
    if (bundleButton) {
      const bundleConfigured = root.dataset.bundleConfigured === 'true';
      const variants = parseJson(root.dataset.bundleVariants || '[]', []);
      const bundleOptionCount = Number(root.dataset.bundleOptionCount || 0);
      const sourceOptions = parseJson(root.dataset.sourceOptions || '[]', []);
      if (bundleConfigured && bundleOptionCount >= 3) {
        const firstColor = normalizedOptions(sourceOptions)[0] || '';
        const hasAvailableVariantForFirstColor = variants.some((variant) => {
          const opts = normalizedOptions(variant.options);
          return opts[0] === firstColor && variant.available === true;
        });
        bundleUnavailable = !hasAvailableVariantForFirstColor;
      } else {
        const bundleAvailable = root.dataset.bundleAvailable === 'true';
        const bundleManaged = root.dataset.bundleInventoryManaged === 'true';
        const bundlePolicy = root.dataset.bundleInventoryPolicy || 'deny';
        const bundleQty = Number(root.dataset.bundleInventoryQuantity || 0);
        bundleUnavailable = !bundleConfigured || !root.dataset.currentBundleVariantId || !bundleAvailable || (bundleManaged && bundlePolicy !== 'continue' && bundleQty < 1);
      }
      selectedComboUnavailable = !root.dataset.currentBundleVariantId || root.dataset.bundleAvailable !== 'true';
      bundleButton.disabled = bundleUnavailable;
      if (bundleUnavailable) bundleButton.setAttribute('aria-disabled', 'true');
      else bundleButton.removeAttribute('aria-disabled');
    }

    showBundleColorSelectors(root);
    if (note) {
      const bundleSelected = selectedQuantity(root) === 3;
      note.hidden = !bundleButton || bundleUnavailable || !bundleSelected || !selectedComboUnavailable;
    }
    const selected = root.querySelector('[data-quantity-offer-option].is-selected');
    if (selected && selected.disabled && oneButton && !oneButton.disabled) selectOption(root, oneButton, true);
  }

  function selectOption(root, button, updateInput = true) {
    if (!button || button.disabled) return;
    root.querySelectorAll('[data-quantity-offer-option]').forEach((option) => {
      const selected = option === button;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    if (updateInput) {
      const input = root.querySelector('[data-quantity-offer-input]');
      if (input) {
        input.value = button.dataset.quantity;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    showBundleColorSelectors(root);
  }

  function initRoot(root) {
    const input = root.querySelector('[data-quantity-offer-input]');
    if (!input) return;
    updateBundleVariant(root, parseJson(root.dataset.sourceOptions || '[]', []));
    renderPrices(root);
    updateAvailability(root);
    const initialButton = root.querySelector(`[data-quantity-offer-option][data-quantity="${input.value || 1}"]`);
    if (initialButton && !initialButton.disabled) selectOption(root, initialButton);
  }

  document.querySelectorAll(rootSelector).forEach(initRoot);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-offer-option]');
    if (!button || button.disabled) return;
    const root = button.closest(rootSelector);
    if (root) {
      selectOption(root, button);
      updateAvailability(root);
    }
  });

  document.addEventListener('change', (event) => {
    const selector = event.target.closest('[data-bundle-color-select]');
    if (!selector) return;
    const root = selector.closest(rootSelector);
    if (!root) return;
    const sourceOptions = parseJson(root.dataset.sourceOptions || '[]', []);
    updateBundleVariant(root, sourceOptions);
    renderPrices(root);
    updateAvailability(root);
  });

  /* Add one bundle variant to cart. It represents three watches with independently selected colors. */
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;

    const formId = form.getAttribute('id');
    const root = Array.from(document.querySelectorAll(rootSelector)).find((candidate) => candidate.dataset.formId === formId);
    if (!root || selectedQuantity(root) !== 3) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const bundleId = root.dataset.currentBundleVariantId;
    const button = root.querySelector('[data-quantity-offer-option][data-quantity="3"]');
    const note = root.querySelector('[data-quantity-stock-note]');
    const submitter = event.submitter;

    if (!bundleId || !button || button.disabled || root.dataset.bundleAvailable !== 'true') {
      if (note) {
        note.textContent = 'Essa combinação de cores não está disponível. Escolha outra cor para cada relógio.';
        note.hidden = false;
      }
      return;
    }

    if (submitter) submitter.setAttribute('aria-busy', 'true');
    if (note) note.hidden = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      const formData = new FormData();
      formData.set('id', String(bundleId));
      formData.set('quantity', '1');
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', `${window.location.pathname}${window.location.search}`);

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || 'Não foi possível adicionar o pacote ao carrinho.');
      }

      if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections || {};
      const addedItems = responseJson.items || [responseJson];
      const successDetail = { items: addedItems, cart: cartContent, onSuccessDo: formData.get('on_success') };

      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: successDetail }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', onSuccessDo: formData.get('on_success'), cart: cartContent }
      }));
    } catch (error) {
      if (note) {
        note.textContent = error?.message || 'Não foi possível adicionar a oferta. Confira as cores e tente novamente.';
        note.hidden = false;
      }
      console.error('[DITORINO quantity offer]', error);
    } finally {
      if (submitter) submitter.removeAttribute('aria-busy');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }, true);

  document.addEventListener('variant:change', (event) => {
    const detail = event.detail || {};
    const formId = detail.formId;
    const variant = detail.variant;
    if (!variant || typeof variant.price === 'undefined') return;
    document.querySelectorAll(rootSelector).forEach((root) => {
      if (formId && root.dataset.formId !== formId) return;
      root.dataset.available = variant.available ? 'true' : 'false';
      root.dataset.inventoryPolicy = variant.inventory_policy || 'deny';
      const hasInventoryQuantity = variant.inventory_quantity !== undefined && variant.inventory_quantity !== null && Number.isFinite(Number(variant.inventory_quantity));
      root.dataset.inventoryManaged = variant.inventory_management && hasInventoryQuantity ? 'true' : 'false';
      root.dataset.inventoryQuantity = hasInventoryQuantity ? String(variant.inventory_quantity) : '999999';
      root.dataset.basePrice = String(variant.price);
      const sourceOptions = Array.isArray(variant.options) ? variant.options : [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
      updateBundleVariant(root, sourceOptions);
      renderPrices(root);
      updateAvailability(root);
    });
  });
})();
